import React, { useState } from 'react';
import { X, Columns3, Check, Scale, FileDown, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePrice } from '@/lib/priceMode';
import { isPlaceholderPrice } from '@/lib/format';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveDoc = (d) => d?.url?.startsWith('/api/') ? `${BACKEND}${d.url}?download=1` : d?.url;
const resolveImg = (url) => url?.startsWith('/api/') ? `${BACKEND}${url}` : url;

/**
 * Sticky comparator bar + full-width modal.
 * Props:
 *  - selected: Product[]
 *  - onRemove(slug)
 *  - onClear()
 *  - onAddToCart(product) (optional)
 */
export default function ProductComparator({ selected, onRemove, onClear, onAddToCart }) {
  const [open, setOpen] = useState(false);
  const { format: formatPrice } = usePrice();

  if (!selected || selected.length === 0) return null;

  // Aggregate all spec keys across products
  const allSpecKeys = Array.from(new Set(
    selected.flatMap(p => Object.keys(p.specs || {}))
  ));

  return (
    <>
      {/* Sticky Bar */}
      <motion.div
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        exit={{ y: 80 }}
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-1rem)]"
        data-testid="comparator-bar"
      >
        <div className="flex items-center gap-3 px-4 py-3 bg-[#0A2540] text-white rounded-2xl shadow-2xl border border-white/10">
          <Scale className="w-5 h-5 text-[#F8D90F]" />
          <div className="flex items-center gap-1.5 overflow-x-auto max-w-xs sm:max-w-md">
            {selected.map(p => (
              <span
                key={p.slug}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 text-[10px] whitespace-nowrap"
              >
                <span className="truncate max-w-[120px]">{p.name}</span>
                <button
                  type="button"
                  onClick={() => onRemove(p.slug)}
                  className="ml-0.5 hover:text-red-400"
                  title="Quitar"
                  aria-label="Quitar"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={selected.length < 2}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0EA5E9] rounded-xl text-xs font-semibold hover:bg-sky-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="comparator-open-btn"
          >
            <Columns3 className="w-3.5 h-3.5" />
            Comparar ({selected.length})
          </button>
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] text-white/60 hover:text-white underline"
            data-testid="comparator-clear"
          >
            Limpiar
          </button>
        </div>
        {selected.length < 2 && (
          <p className="text-[10px] text-white/70 text-center mt-1">Selecciona al menos 2 para comparar</p>
        )}
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
            onClick={() => setOpen(false)}
            data-testid="comparator-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-[#0EA5E9]" />
                  <h2 className="text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>Comparador de productos ({selected.length})</h2>
                </div>
                <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Cerrar" data-testid="comparator-close">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <div className="overflow-auto flex-1">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr>
                      <th className="w-36 p-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-200"></th>
                      {selected.map(p => (
                        <th key={p.slug} className="p-3 border-b border-slate-200 align-top text-left min-w-[180px]">
                          <div className="space-y-2">
                            <div className="aspect-[4/3] rounded-lg bg-slate-50 overflow-hidden flex items-center justify-center">
                              {p.image ? (
                                <img src={resolveImg(p.image)} alt={p.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-3xl font-bold text-slate-300" style={{fontFamily:'Syne,sans-serif'}}>{p.brand?.[0] || '?'}</span>
                              )}
                            </div>
                            <div>
                              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{p.brand}</div>
                              <div className="text-sm font-semibold text-[#0A2540] leading-tight mt-0.5">{p.name}</div>
                              {p.tier && <div className="inline-block mt-1 px-2 py-0.5 rounded-full bg-sky-50 text-[#0EA5E9] text-[9px] font-semibold uppercase">{p.tier}</div>}
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">Precio</td>
                      {selected.map(p => (
                        <td key={p.slug} className="p-3 border-b border-slate-100 align-top">
                          {isPlaceholderPrice(p.price_display || p.price) ? (
                            <div className="text-base font-bold text-[#0EA5E9]" style={{fontFamily:'Syne,sans-serif'}}>Cotizar</div>
                          ) : (
                            <div className="text-lg font-bold text-[#0A2540] tabular-nums" style={{fontFamily:'Syne,sans-serif'}}>{formatPrice(p.price_display || p.price) || '—'}</div>
                          )}
                          {p.liquidacion && p.price_original && !isPlaceholderPrice(p.price_original) && (
                            <div className="text-[10px] text-slate-400 line-through tabular-nums">{formatPrice(p.price_original)}</div>
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="p-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">Resumen</td>
                      {selected.map(p => (
                        <td key={p.slug} className="p-3 text-xs text-slate-600 leading-relaxed border-b border-slate-100 align-top">{p.summary || '—'}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">Características</td>
                      {selected.map(p => (
                        <td key={p.slug} className="p-3 text-xs border-b border-slate-100 align-top">
                          {Array.isArray(p.features) && p.features.length > 0 ? (
                            <ul className="space-y-1">
                              {p.features.slice(0, 5).map((f, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-slate-600">
                                  <Check className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                      ))}
                    </tr>
                    {allSpecKeys.length > 0 && allSpecKeys.map((key, idx) => (
                      <tr key={key} className={idx % 2 ? 'bg-slate-50/50' : ''}>
                        <td className="p-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100 font-mono normal-case">{key}</td>
                        {selected.map(p => (
                          <td key={p.slug} className="p-3 text-xs text-slate-700 border-b border-slate-100 align-top">
                            {p.specs?.[key] || <span className="text-slate-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="p-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Documentos</td>
                      {selected.map(p => (
                        <td key={p.slug} className="p-3 align-top">
                          <div className="flex flex-wrap gap-1.5">
                            {(p.spec_doc?.url || p.spec_pdf) && (
                              <a href={resolveDoc(p.spec_doc) || p.spec_pdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded bg-sky-50 text-[#0EA5E9] hover:bg-sky-100">
                                <FileDown className="w-3 h-3" />Ficha
                              </a>
                            )}
                            {(p.brochure_doc?.url || p.brochure_pdf) && (
                              <a href={resolveDoc(p.brochure_doc) || p.brochure_pdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600 hover:bg-slate-200">
                                <FileDown className="w-3 h-3" />Brochure
                              </a>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                    {onAddToCart && (
                      <tr className="bg-sky-50/50">
                        <td className="p-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Acción</td>
                        {selected.map(p => (
                          <td key={p.slug} className="p-3">
                            <button
                              type="button"
                              onClick={() => onAddToCart(p)}
                              className="w-full inline-flex items-center justify-center gap-1 py-2 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 transition-colors"
                              data-testid={`comparator-add-${p.slug}`}
                            >
                              <ShoppingCart className="w-3.5 h-3.5" />Agregar
                            </button>
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                <p className="text-[11px] text-slate-500">💡 Precios en pesos chilenos (CLP) + IVA. Contáctanos para cotización oficial.</p>
                <button onClick={() => { onClear(); setOpen(false); }} className="text-[11px] text-slate-500 hover:text-red-500">
                  Limpiar comparación
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
